import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/components/SmoothScroll";

const LINKS = [
  { label: "Capabilities", to: "/", hash: "services" },
  { label: "Equipment", to: "/", hash: "equipment" },
  { label: "Prime Ready", to: "/", hash: "prime" },
  { label: "Careers", to: "/careers" },
  { label: "Resources", to: "/resources" },
  { label: "Contact", to: "/", hash: "contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (link) => {
    setOpen(false);
    if (link.hash) {
      if (loc.pathname !== "/") {
        nav("/");
        setTimeout(() => scrollToId(link.hash), 450);
      } else {
        scrollToId(link.hash);
      }
    } else {
      nav(link.to);
      window.scrollTo({ top: 0 });
    }
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 bg-white border-b border-wcc-ink/10 transition-shadow duration-300 ${
        scrolled ? "shadow-[0_8px_30px_rgba(10,18,36,0.08)]" : ""
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-[86px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group" onClick={() => window.scrollTo({ top: 0 })}>
          <img src="/assets/logo.png" alt="West Coral Communications" className="h-16 sm:h-[68px] w-auto" />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display font-semibold text-wcc-ink text-base tracking-tight uppercase">West Coral</span>
            <span className="mono text-[10px] tracking-[0.25em] text-wcc-gray uppercase">Communications</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => go(l)}
              className="px-4 py-2 text-sm font-medium text-wcc-ink/70 hover:text-wcc-red transition-colors relative"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/portal/login"
            data-testid="nav-owner-login"
            className="mono text-[11px] uppercase tracking-widest text-wcc-gray hover:text-wcc-red transition-colors"
          >
            Owner Login
          </Link>
          <Link
            to="/onboarding"
            data-testid="nav-employee-portal"
            className="group inline-flex items-center gap-1.5 bg-wcc-red text-white text-sm font-semibold px-5 py-2.5 rounded-sm hover:bg-wcc-red-deep transition-colors"
          >
            Employee Portal
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </div>

        <button
          className="lg:hidden text-wcc-ink p-2"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-wcc-ink/10 overflow-hidden"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.label}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => go(l)}
                  className="text-left py-3 text-wcc-ink/80 border-b border-wcc-ink/10 font-medium"
                >
                  {l.label}
                </button>
              ))}
              <div className="flex flex-col gap-3 pt-4">
                <Link to="/onboarding" onClick={() => setOpen(false)} className="bg-wcc-red text-white text-center font-semibold py-3 rounded-sm" data-testid="nav-mobile-employee-portal">
                  Employee Portal
                </Link>
                <Link to="/portal/login" onClick={() => setOpen(false)} className="text-center mono text-xs uppercase tracking-widest text-wcc-gray py-2" data-testid="nav-mobile-owner-login">
                  Owner Login
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
